// Workspace avatar for the far-left column.
//
// Sprint 11b.1 — initial Slack-style 60 px column with deterministic
// auto-color (FNV-1a → HSL hue) and one-letter initial.
// Sprint 11b.2 — supports user-customized 2-character initials and
// 12-color palette via `avatarColor` / `avatarInitials`. Resolution
// happens in `lib/avatar-defaults.resolveAvatar()` so the same logic
// powers the live preview in `EditProjectModal`.

import { resolveAvatar } from '../../lib/avatar-defaults';

export interface WorkspaceAvatarProps {
  slug: string;
  name: string;
  unreadCount: number;
  isActive: boolean;
  onClick?: () => void;
  avatarColor: string | null;
  avatarInitials: string | null;
  // When true, the wrapper button is replaced with a non-interactive div.
  // Used by the drag preview overlay where the avatar must not steal
  // pointer events from the dnd-kit drag layer.
  presentational?: boolean;
}

// Re-exported so existing call sites (S11b.1 tests) keep working with
// the colour helper. The implementation moved to lib/avatar-defaults.ts.
export { autoColor as avatarColor } from '../../lib/avatar-defaults';

export function WorkspaceAvatar({
  slug,
  name,
  unreadCount,
  isActive,
  onClick,
  avatarColor,
  avatarInitials,
  presentational,
}: WorkspaceAvatarProps) {
  const resolved = resolveAvatar({ name, slug, avatarColor, avatarInitials });

  const inner = (
    <span className="text-body font-550 select-none tracking-tight">
      {resolved.initials}
    </span>
  );

  const baseStyle = {
    width: 40,
    height: 40,
    backgroundColor: resolved.bg,
    color: resolved.fg,
    borderRadius: isActive ? 'var(--radius)' : 'calc(var(--radius) + 4px)',
    outline: isActive ? '2px solid hsl(var(--action))' : 'none',
    outlineOffset: isActive ? '2px' : '0',
  } as const;

  return (
    <div className="relative flex items-center justify-center w-full py-1">
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-action"
          data-testid="workspace-active-bar"
        />
      )}
      {presentational ? (
        <div
          className="relative flex items-center justify-center transition-all duration-150"
          style={baseStyle}
          data-testid={`workspace-avatar-${slug}`}
          aria-label={name}
          title={name}
        >
          {inner}
        </div>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="relative flex items-center justify-center transition-all duration-150"
          style={baseStyle}
          data-testid={`workspace-avatar-${slug}`}
          aria-label={name}
          aria-current={isActive ? 'true' : undefined}
          title={name}
        >
          {inner}
        </button>
      )}

      {unreadCount > 0 && (
        <span
          className="absolute -top-0.5 right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-status-info text-caption font-550 leading-none text-white"
          data-testid={`workspace-badge-${slug}`}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </div>
  );
}
